/**
 * Estado compartido del asistente WhatsApp (WhatsAppIntakeSheet) entre las
 * múltiples instancias montadas en una misma página (hero, sticky mobile,
 * header, footer, CTA final, sidebar de apartamento…).
 *
 * PROBLEMA QUE RESUELVE
 * Cada instancia del sheet tenía su propio `useState`, así que las fechas y
 * huéspedes que el cliente elegía en un botón NO existían en el sheet de otro
 * botón. Si elegía fechas en el hero y terminaba enviando desde el botón
 * flotante, el envío salía "genérico" (sin fechas) y el guard de
 * `fireWspReservasConversion` (lib/analytics/gtag-conversion.ts) bloqueaba la
 * conversión calificada de Google Ads. Caso real: lead de Ads 2026-06-11 18:21
 * en /edificios/nuevo-conquistador (rageclick en el contador de huéspedes →
 * reabrió desde el sticky → envío genérico → 0 conversiones).
 *
 * SOLUCIÓN
 * Fuente única en sessionStorage (mismo patrón que el dedup de conversión del
 * propio gtag-conversion.ts). El sheet hidrata al abrir y persiste al cambiar,
 * así la selección viaja entre todas las instancias y entre navegaciones
 * dentro de la misma pestaña. NO cambia el criterio de conversión: se sigue
 * contando solo cuando hay fechas + huéspedes.
 *
 * Módulo puro y testeable: las funciones aceptan un `IntakeStorage` inyectable
 * (default: window.sessionStorage). Ver tests/intake-state.test.ts.
 *
 * Versión: v1.0.0
 * Última actualización: 2026-06-11
 */

export type IntakeChildAge = { id: string; age: '4-' | '5+' };

export interface IntakeStayState {
  /** Check-in en ISO `yyyy-mm-dd`. */
  arrival: string;
  /** Noches (la salida se deriva en el componente con calculateDeparture). */
  nights: number;
  numAdults: number;
  childrenAges: IntakeChildAge[];
}

/** Subconjunto de Storage que necesitamos — facilita inyectar un fake en tests. */
export interface IntakeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const STORAGE_KEY = 'wsp_intake_state_v1';
const MAX_NIGHTS = 60;
const MAX_ADULTS = 30;
const MAX_CHILDREN = 20;

function resolveStorage(storage?: IntakeStorage | null): IntakeStorage | null {
  if (storage) return storage;
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    // Safari en modo privado o storage bloqueado por política: degradar sin romper.
    return null;
  }
}

function isValidArrival(value: unknown): value is string {
  return isValidISODate(value);
}

function clampInt(value: unknown, min: number, max: number): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const n = Math.round(value);
  if (n < min || n > max) return null;
  return n;
}

function sanitizeChildren(value: unknown): IntakeChildAge[] {
  if (!Array.isArray(value)) return [];
  const out: IntakeChildAge[] = [];
  for (const item of value) {
    if (out.length >= MAX_CHILDREN) break;
    if (
      item &&
      typeof item === 'object' &&
      typeof (item as IntakeChildAge).id === 'string' &&
      ((item as IntakeChildAge).age === '4-' || (item as IntakeChildAge).age === '5+')
    ) {
      out.push({ id: (item as IntakeChildAge).id, age: (item as IntakeChildAge).age });
    }
  }
  return out;
}

/**
 * Persiste la selección SOLO si hay fecha de entrada válida — sin fecha no hay
 * nada que "perder" entre instancias y la conversión calificada no aplicaría.
 * Nunca lanza: un fallo de storage no debe romper la UX.
 */
export function saveIntakeStayState(state: IntakeStayState, storage?: IntakeStorage | null): void {
  const s = resolveStorage(storage);
  if (!s) return;
  if (!isValidArrival(state.arrival)) return;
  const nights = clampInt(state.nights, 1, MAX_NIGHTS);
  const numAdults = clampInt(state.numAdults, 1, MAX_ADULTS);
  if (nights === null || numAdults === null) return;
  try {
    s.setItem(
      STORAGE_KEY,
      JSON.stringify({
        arrival: state.arrival,
        nights,
        numAdults,
        childrenAges: sanitizeChildren(state.childrenAges),
      }),
    );
  } catch {
    // Cuota llena / bloqueado: ignorar, es solo conveniencia de tracking/UX.
  }
}

/**
 * Devuelve la selección guardada validada, o `null` si no hay nada usable.
 * El consumidor aplica los campos tal cual (todos válidos por construcción).
 */
export function loadIntakeStayState(storage?: IntakeStorage | null): IntakeStayState | null {
  const s = resolveStorage(storage);
  if (!s) return null;
  let raw: string | null;
  try {
    raw = s.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;

  const p = parsed as Record<string, unknown>;
  if (!isValidArrival(p.arrival)) return null;
  const nights = clampInt(p.nights, 1, MAX_NIGHTS);
  const numAdults = clampInt(p.numAdults, 1, MAX_ADULTS);
  if (nights === null || numAdults === null) return null;

  return {
    arrival: p.arrival,
    nights,
    numAdults,
    childrenAges: sanitizeChildren(p.childrenAges),
  };
}

/** Limpia el estado compartido (ej. tras enviar/reservar, si se quisiera). */
export function clearIntakeStayState(storage?: IntakeStorage | null): void {
  const s = resolveStorage(storage);
  if (!s) return;
  try {
    s.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}
import { isValidISODate } from '@/lib/dateUtils';
